import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, unlockScroll } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** Filename-based section — safe for Section Rendering API (unlike JSON template keys). */
const QUICK_ADD_SECTION_ID = 'section-rendering-quick-add';

export class QuickAddComponent extends Component {
  /** Shared across all cards so prefetch on one instance helps others / the click path. */
  /** @type {Map<string, Element>} */
  static #contentCache = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  static #inflightRequests = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);

    // Prefetch on intent — before click — so Choose can open from cache.
    this.addEventListener('pointerenter', this.#prefetchProductPage, { passive: true });
    this.addEventListener('focusin', this.#prefetchProductPage);
    this.addEventListener('touchstart', this.#prefetchProductPage, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.removeEventListener('pointerenter', this.#prefetchProductPage);
    this.removeEventListener('focusin', this.#prefetchProductPage);
    this.removeEventListener('touchstart', this.#prefetchProductPage);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Cart updates used to wipe per-instance cache and make the next Choose slow.
   * Shared section cache stays; form state is fresh enough for quick-add UX.
   */
  #handleCartUpdate = () => {};

  #prefetchProductPage = () => {
    const url = this.productPageUrl;
    if (!url || this.dataset.usesSellingPlans === 'true') return;
    void this.#ensureProductGrid(url);
  };

  /**
   * @param {string} productPageUrl
   * @returns {string}
   */
  #cacheKey(productPageUrl) {
    try {
      const url = new URL(productPageUrl, window.location.origin);
      url.searchParams.delete('section_id');
      return `${url.pathname}${url.search}`;
    } catch {
      return productPageUrl;
    }
  }

  /**
   * @param {string} productPageUrl
   * @returns {string}
   */
  #buildSectionUrl(productPageUrl) {
    const url = new URL(productPageUrl, window.location.origin);
    url.searchParams.set('section_id', QUICK_ADD_SECTION_ID);
    return url.toString();
  }

  /**
   * Returns cached product grid (or fetches the lightweight quick-add section).
   * Dedupes concurrent requests for the same product URL.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  async #ensureProductGrid(productPageUrl) {
    const key = this.#cacheKey(productPageUrl);
    const cached = QuickAddComponent.#contentCache.get(key);
    if (cached) return cached;

    const inflight = QuickAddComponent.#inflightRequests.get(key);
    if (inflight) return inflight;

    const request = this.#fetchAndCacheProductGrid(productPageUrl, key);
    QuickAddComponent.#inflightRequests.set(key, request);

    try {
      return await request;
    } finally {
      QuickAddComponent.#inflightRequests.delete(key);
    }
  }

  /**
   * @param {string} productPageUrl
   * @param {string} key
   * @returns {Promise<Element | null>}
   */
  async #fetchAndCacheProductGrid(productPageUrl, key) {
    try {
      const html = await this.#fetchProductDocument(productPageUrl);
      if (!html) return null;

      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) return null;

      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      QuickAddComponent.#contentCache.set(key, productGrid);
      return productGrid;
    } catch (error) {
      console.warn('[quick-add] Failed to load product content:', error);
      return null;
    }
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.toggleAttribute('loading', true);

    try {
      const productGrid = await this.#ensureProductGrid(currentUrl);

      if (productGrid) {
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#openQuickAddModal();
        return;
      }

      // Fallback: product template has no [data-product-grid-content] (e.g. custom PDP only).
      // Avoid opening an empty white modal — go to the product page instead.
      if (currentUrl) {
        window.location.href = currentUrl;
      }
    } finally {
      this.removeAttribute('loading');
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Prefetches and caches product grid HTML for a product URL.
   * Used by product-card hover/focus and by Choose click.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;
    await this.#ensureProductGrid(productPageUrl);
    return null;
  }

  /**
   * Fetches lightweight quick-add section HTML (falls back to full product page).
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async #fetchProductDocument(productPageUrl) {
    if (!productPageUrl) return null;

    const sectionResponse = await fetch(this.#buildSectionUrl(productPageUrl));
    if (sectionResponse.ok) {
      const sectionText = await sectionResponse.text();
      const sectionHtml = new DOMParser().parseFromString(sectionText, 'text/html');
      if (sectionHtml.querySelector('[data-product-grid-content]')) {
        return sectionHtml;
      }
    }

    // Fallback: full product page if section is unavailable
    const response = await fetch(productPageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    return new DOMParser().parseFromString(responseText, 'text/html');
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const buyButtonsBlock = productGrid.querySelector('.buy-buttons-block');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }

      // Move entire buy block (not only form) so ATC + PayPal stay in normal flow on iOS
      const actions = document.createElement('div');
      actions.classList.add('ppx-qa-actions');
      if (buyButtonsBlock) {
        actions.appendChild(buyButtonsBlock);
      } else if (productFormComponent) {
        actions.appendChild(productFormComponent);
      }
      if (actions.childElementCount) {
        productGrid.appendChild(actions);
      }

      productDetails?.remove();
      modalContent?.classList.add('ppx-qa-mobile');
    } else {
      modalContent?.classList.remove('ppx-qa-mobile');
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal without
   * dispatching change (avoids a second section fetch on open).
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dataset.currentChecked = 'true';
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('change', this.#handleLocalVariantChange, { signal: this.#abortController.signal });

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Direct close binding — works even if delegated on:click is blocked
    const closeBtn = this.querySelector('[ref="closeButton"], .quick-add-modal__close');
    closeBtn?.addEventListener('click', this.#onCloseButtonClick, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #onCloseButtonClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    void this.closeDialog();
  };

  /**
   * Close immediately. Must not leave dialog visible via CSS after close().
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog) return;

    try {
      dialog.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
    } catch {
      /* ignore */
    }

    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';

    // Close first (leave top layer), then unlock scroll
    if (dialog.open) {
      dialog.close();
    }
    dialog.removeAttribute('open');
    dialog.style.display = 'none';

    unlockScroll(dialog);
    this.dispatchEvent(new DialogCloseEvent());

    // Allow next open to use UA / CSS display again
    requestAnimationFrame(() => {
      if (!dialog.open) dialog.style.display = '';
    });
  };

  showDialog() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;
    dialog.style.display = '';
    super.showDialog();
  }

  /**
   * Instant PDP-style update for price + image (no wait for section fetch).
   * @param {Event} event
   */
  #handleLocalVariantChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLSelectElement)) return;
    if (!target.closest('variant-picker')) return;

    const picker = target.closest('variant-picker');
    if (!picker) return;

    // Active swatch styling
    const fieldset = target.closest('fieldset');
    if (fieldset && target instanceof HTMLInputElement) {
      fieldset.querySelectorAll('.pv-qa-swatch').forEach((el) => el.classList.remove('is-active'));
      target.closest('.pv-qa-swatch')?.classList.add('is-active');
      const label = fieldset.querySelector('[data-pv-qa-optlabel]');
      if (label) label.textContent = target.value;
    }

    const variant = this.#findSelectedVariant(picker);
    if (!variant) return;

    this.#applyVariantLocally(variant, target);
  };

  /**
   * @param {Element} picker
   * @returns {Record<string, any> | null}
   */
  #findSelectedVariant(picker) {
    const variantsScript = picker.querySelector('script[data-quick-add-variants]');
    if (!variantsScript?.textContent) return null;

    /** @type {Array<Record<string, any>>} */
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch {
      return null;
    }

    const selected = {};
    picker.querySelectorAll('input[type="radio"]:checked, select').forEach((el) => {
      if (el instanceof HTMLInputElement) {
        const pos = el.dataset.optionPosition || el.dataset.inputId?.split('-')[0];
        if (pos) selected[pos] = el.value;
      } else if (el instanceof HTMLSelectElement) {
        const opt = el.selectedOptions[0];
        const pos = opt?.dataset.inputId?.split('-')[0];
        if (pos) selected[pos] = el.value;
      }
    });

    return (
      variants.find((v) => {
        const opts = [v.option1, v.option2, v.option3];
        for (const [pos, val] of Object.entries(selected)) {
          const idx = Number(pos) - 1;
          if (idx >= 0 && opts[idx] !== val) return false;
        }
        return true;
      }) ?? null
    );
  }

  /**
   * @param {Record<string, any>} variant
   * @param {Element} sourceInput
   */
  #applyVariantLocally(variant, sourceInput) {
    // Variant id for add-to-cart
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantInput && variant.id) {
      variantInput.value = String(variant.id);
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Price
    this.#updatePriceLocally(variant);

    // Featured image — instant swap like PDP
    this.#updateImageLocally(variant, sourceInput);

    // ATC availability
    const addBtn = /** @type {HTMLButtonElement | null} */ (this.querySelector('.add-to-cart-button[type="submit"]'));
    if (addBtn) {
      addBtn.disabled = !variant.available;
    }
  }

  /** @param {Record<string, any>} variant */
  #updatePriceLocally(variant) {
    const priceContainer = this.querySelector('product-price [ref="priceContainer"]');
    if (!(priceContainer instanceof HTMLElement)) return;

    const moneyScript = this.querySelector('script[data-quick-add-money]');
    let format = '{{amount}}';
    let currency = 'USD';
    try {
      if (moneyScript?.textContent) {
        const meta = JSON.parse(moneyScript.textContent);
        format = meta.format || format;
        currency = meta.currency || currency;
      }
    } catch {
      /* keep defaults */
    }

    const priceText = formatMoney(variant.price, format, currency);
    const compare = variant.compare_at_price;
    const hasCompare = Boolean(compare && compare > variant.price);

    if (hasCompare) {
      priceContainer.innerHTML = `
        <span class="price-item__group price-item--regular compare-at-price">
          <span class="visually-hidden">Regular price&nbsp;</span>${formatMoney(compare, format, currency)}
        </span>
        <span class="price-item__group price">
          <span class="visually-hidden">Sale price&nbsp;</span>${priceText}
        </span>`;
    } else {
      priceContainer.innerHTML = `<span class="price">${priceText}</span>`;
    }
  }

  /**
   * @param {Record<string, any>} variant
   * @param {Element} sourceInput
   */
  #updateImageLocally(variant, sourceInput) {
    const mediaId =
      (sourceInput instanceof HTMLElement && sourceInput.dataset.optionMediaId) ||
      variant.featured_media?.id ||
      variant.featured_image?.id;

    const gallery = this.querySelector('media-gallery');
    if (gallery && mediaId && typeof gallery.slideshow?.select === 'function') {
      const slides = gallery.slideshow.refs?.slides || [];
      const index = slides.findIndex(
        (slide) =>
          slide.getAttribute('slide-id') === String(mediaId) ||
          Boolean(slide.querySelector(`[data-media-id="${mediaId}"]`))
      );
      if (index >= 0) {
        const slideId = slides[index]?.getAttribute('slide-id');
        if (slideId) gallery.slideshow.select({ id: slideId }, undefined, { animate: false });
        else gallery.slideshow.select(index, undefined, { animate: false });
        return;
      }
    }

    // Fallback: swap visible image src from variant featured_image
    const img =
      this.querySelector('slideshow-slide[aria-hidden="false"] img') ||
      this.querySelector('.product-information__media img');
    const src = variant.featured_image?.src;
    if (img instanceof HTMLImageElement && src) {
      const url = src.startsWith('//') ? `https:${src}` : src;
      img.src = url;
      img.removeAttribute('srcset');
    }
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
